import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginData, logout } from "../stores/auth";
import { axios, errorResponse } from "../helpers";

const useTransactionReports = () => {
  const dispatch = useDispatch();
  const dataLogin = useSelector(loginData);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    transaction_id: null,
    product_name: null,
    category: null,
    order_type: null,
    start_date: null,
    end_date: null,
  });

  // Transform API response to UI format
  const transformData = (apiData) => {
    return apiData.map((item) => {
      const fabricName = item.order_details?.[0]?.fabric?.fabric_name;
      const productName =
        fabricName ||
        (item.product_names?.length ? item.product_names.join(", ") : "N/A");
      const quantity =
        item.order_details?.[0]?.qty ?? item.product_names?.length ?? 0;

      console.log("QTY CHECK", item.order_details, quantity);

      return {
        _id: item._id,
        id: item.transaction_id,
        product: productName,
        category:
          item.categories?.length > 0 ? item.categories.join(", ") : "N/A",
        date: new Date(item.transaction_date).toISOString().split("T")[0],
        orderStatus: item.order_status,
        paymentStatus: item.payment_status,
        orderType: item.order_type,
        total: item.total_transaction,
        shipping: item.shipping_by || "N/A",
        customer: item.customer_name,
        quantity,
        totalDiscount: item.total_discount,
        totalShipping: item.total_shipping,
        shippingBy: item.shipping_by,
        customerEmail: item.customer_email,
        deliveryCity: item.delivery_city,
        deliveryProvince: item.delivery_province,
      };
    });
  };

  // Fetch transaction reports
  const handleFetchReports = async () => {
    try {
      setIsLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", filters.page);
      params.append("limit", filters.limit);

      if (filters.transaction_id) {
        params.append("transaction_id", filters.transaction_id);
      }
      if (filters.product_name) {
        params.append("product_name", filters.product_name);
      }
      if (filters.category) {
        params.append("category", filters.category);
      }
      if (filters.order_type) {
        params.append("order_type", filters.order_type);
      }
      if (filters.start_date) {
        params.append("start_date", filters.start_date);
      }
      if (filters.end_date) {
        params.append("end_date", filters.end_date);
      }

      const response = await axios.get(
        `/v1/reports/transactions/?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${dataLogin.data.token}` },
        }
      );

      if (response.data.success) {
        const transformedData = transformData(response.data.data);
        setData(transformedData);
        setTotalData(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
        setError("");
      } else {
        setError(response.data.message || "Failed to fetch reports");
      }
    } catch (err) {
      const errorMsg = errorResponse(err);
      setError(errorMsg);

      // Handle unauthorized error
      if (err.response?.status === 401 || err.response?.status === 403) {
        dispatch(logout());
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    handleFetchReports();
  }, [filters]);

  // Update filters with search term
  const setSearchTerm = (searchTerm) => {
    setFilters({
      ...filters,
      page: 1,
      transaction_id: searchTerm || null,
    });
  };

  // Update filters with category
  const setCategory = (category) => {
    setFilters({
      ...filters,
      page: 1,
      category: category === "all" ? null : category,
    });
  };

  // Update filters with order type
  const setOrderType = (orderType) => {
    setFilters({
      ...filters,
      page: 1,
      order_type: orderType === "all" ? null : orderType,
    });
  };

  // Update filters with date range
  const setDateRange = (startDate, endDate) => {
    setFilters({
      ...filters,
      page: 1,
      start_date: startDate ? startDate.toISOString().split("T")[0] : null,
      end_date: endDate ? endDate.toISOString().split("T")[0] : null,
    });
  };

  // Update pagination
  const setPagination = (page, limit) => {
    setFilters({
      ...filters,
      page,
      limit,
    });
  };

  // Get unique categories from data
  const getCategories = () => {
    const categories = new Set();
    data.forEach((item) => {
      if (item.category !== "N/A") {
        item.category.split(", ").forEach((cat) => categories.add(cat));
      }
    });
    return Array.from(categories);
  };

  // Get unique order types from data
  const getOrderTypes = () => {
    const types = new Set();
    data.forEach((item) => {
      types.add(item.orderType);
    });
    return Array.from(types);
  };

  return {
    data,
    isLoading,
    error,
    totalData,
    totalPages,
    filters,
    setSearchTerm,
    setCategory,
    setOrderType,
    setDateRange,
    setPagination,
    getCategories,
    getOrderTypes,
  };
};

export default useTransactionReports;
